import React, {useEffect, useMemo, useState} from 'react';
import {getUserFriendlyErrorMessage} from '@/utils/errorHandling.ts';
import {
  Button,
  ButtonVariant,
  FormItem,
  InputType,
  useKeyboardShortcut,
  useModal,
  useToast
} from '@fs/uikit-react';
import styles from './PersonModal.module.css';
import {Person} from '../../../types/person.ts';
import {PersonApi} from '../../../api/PersonApi.ts';
import {Gender, GENDER_OPTIONS} from '../../../types/personConstants.ts';
import {ApiError} from '@/services/api';
import {usePersonModalCategories} from './usePersonModalCategories.ts';
import {CategoryShort} from "@/shared/model/categoryShort.ts";

export enum ModalMode {
  CREATE = 'CREATE',
  EDIT = 'EDIT',
}

interface PersonModalProps {
  mode: ModalMode;
  person?: Person;
  onSuccess: () => void;
}

const PersonModal: React.FC<PersonModalProps> = ({mode, person, onSuccess}) => {
  const {closeModal} = useModal();
  const toast = useToast();

  const [firstName, setFirstName] = useState(person?.firstName || '');
  const [lastName, setLastName] = useState(person?.lastName || '');
  const [fathersName, setFathersName] = useState(person?.fathersName || '');
  const [gender, setGender] = useState<Gender>((person?.gender as Gender) || Gender.MALE);
  const [comment, setComment] = useState(person?.comment || '');

  const [isLoading, setIsLoading] = useState(false);

  const [categories, setCategories] = useState<CategoryShort[]>([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>(
    person?.categories ? person.categories.map((c) => c.id) : []
  );

  const selectedCategories: CategoryShort[] = categories.filter(c => selectedCategoryIds.includes(c.id));

  usePersonModalCategories(setCategories);

  const categoryOptions = useMemo(() => (
    categories.map((c) => ({ value: c.id, label: c.name }))
  ), [categories]);
  
  const handleSubmit = async () => {
    if (!firstName && !lastName) {
      toast.showErrorToast('Помилка валідації', 'Будь ласка, заповніть хоча б Прізвище або Ім\'я');
      return;
    }

    setIsLoading(true);

    const personData: Person = {
      firstName: firstName,
      lastName: lastName,
      fathersName: fathersName,
      gender: gender,
      comment: comment,
      categories: selectedCategories
    };

    try {
      if (mode === ModalMode.EDIT && person?.id) {
        await PersonApi.update(person.id, personData);
        toast.showSuccessToast('Успішно', 'Особу успішно оновлено');
      } else {
        await PersonApi.create(personData);
        toast.showSuccessToast('Успішно', 'Особу успешно створено');
      }
      closeModal();
      onSuccess();
    } catch (error) {
      console.error('Error saving person:', error);
      toast.showErrorToast(
        mode === ModalMode.EDIT ? 'Помилка оновлення особи' : 'Помилка створення особи',
        getUserFriendlyErrorMessage(error as ApiError)
      );
    } finally {
      setIsLoading(false);
    }
  };

  useKeyboardShortcut('Enter', [], handleSubmit);

  return (
    <div className={styles.modalContent}>
      <div className={styles.row}>
        <FormItem
          label="Прізвище"
          type={InputType.TEXT}
          value={lastName}
          onChange={(e) => { setLastName(e.target.value); }}
        />
      </div>
      <div className={styles.row}>
        <FormItem
          label="Ім'я"
          type={InputType.TEXT}
          value={firstName}
          onChange={(e) => { setFirstName(e.target.value); }}
        />
      </div>

      <div className={styles.row}>
        <FormItem
          label="По батькові"
          type={InputType.TEXT}
          value={fathersName}
          onChange={(e) => { setFathersName(e.target.value); }}
        />
      </div>

      <div className={styles.row}>
        <FormItem
          label="Категорії"
          type={InputType.MULTISELECT_SEARCHABLE}
          value={selectedCategoryIds}
          onChange={(e) => { setSelectedCategoryIds(e.target.value as unknown as string[]); }}
          options={categoryOptions}
        />
      </div>

      <div className={styles.row}>
        <FormItem
          label="Стать"
          type={InputType.SELECT}
          value={gender}
          onChange={(e) => { setGender(e.target.value as Gender); }}
          options={GENDER_OPTIONS}
        />
      </div>

      <div className={styles.row}>
        <FormItem
          label="Коментар"
          type={InputType.TEXT_AREA}
          value={comment}
          onChange={(e) => { setComment(e.target.value); }}
        />
      </div>

      <div className={styles.buttonGroup}>
        <Button
          title={isLoading ? 'Створення...' : 'Створити'}
          onClick={handleSubmit}
          loading={isLoading}
        />
        <Button
          title="Скасувати"
          onClick={closeModal}
          variant={ButtonVariant.SECONDARY}
        />
      </div>
    </div>
  );
};

export default PersonModal;
